<script setup lang="ts">
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import { computed, ref } from 'vue';
import { CompromissoForm } from '~/lib/fw';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  pastaCodigos?: string[];
  compromisso?: any;
  fluxo?: any;
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const mostrarHistoricoSolicitacoes = ref<string>('h');
const colunas: GraphqlDatatableColumn[] = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
];

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});
</script>

<template>
  <CompromissoForm
    action="acompanhamento-pagamento-honorarios/solicitar-acompanhamento-pagamento-honorarios"
    :pasta-codigos="props.pastaCodigos ?? []"
    :compromisso-codigos="props.compromisso ? [props.compromisso.codigo] : []"
    :wait-for-result="true"
    :show-upload-files="true"
    :submit-button-disabled="buttonsDisabled"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-collapse
        v-if="props.pastaCodigos?.length === 1"
        v-model:active-key="mostrarHistoricoSolicitacoes"
        style="margin-bottom: 24px"
      >
        <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
          <CompromissoGqlDatatable
            :columns="colunas"
            :show-dates-filter="false"
            :show-reload-button="false"
            :show-status-search-bar="false"
            :show-filter="false"
            :variables="{
              order: [{ dataInicio: 'DESC' }],
              where: {
                tipo: {
                  eq: 140,
                },
                fluxo: {
                  pastaCodigo: {
                    in: [props.pastaCodigos[0]],
                  },
                },
              },
            }"
          />
        </a-collapse-panel>
      </a-collapse>

      <a-form-item
        name="valor"
        label="Valor (R$)"
        :rules="[
          {
            message: 'Valor é obrigatório',
            required: true,
            type: 'number',
            trigger: 'blur',
          },
        ]"
      >
        <CurrencyInput
          v-model="model.valor"
          :options="{
            locale: 'pt-BR',
            currency: 'BRL',
            currencyDisplay: 'symbol',
            hideCurrencySymbolOnFocus: false,
            hideGroupingSeparatorOnFocus: false,
            hideNegligibleDecimalDigitsOnFocus: false,
            autoDecimalDigits: true,
          }"
        />
      </a-form-item>

      <a-form-item
        name="parcelas"
        label="Parcelas"
        :rules="[
          {
            message: 'Parcelas é obrigatória',
            required: true,
            type: 'number',
          },
        ]"
      >
        <a-input-number
          v-model:value="model.parcelas"
          :min="1"
          :max="100"
          :step="1"
          :precision="0"
          :controls="false"
        />
      </a-form-item>

      <a-form-item
        name="dataVencimento"
        label="Vencimento"
        :rules="[
          {
            message: 'Data do vencimento é obrigatório',
            required: true,
          },
        ]"
      >
        <a-date-picker
          v-model:value="model.dataVencimento"
          format="DD/MM/YYYY"
          value-format="DD/MM/YYYY"
          placeholder="Selecione uma data"
          style="width: 100%"
        />
      </a-form-item>

      <a-form-item label="Observação" name="observacao">
        <a-textarea v-model:value="model.observacao" :rows="4" />
      </a-form-item>
    </template>
  </CompromissoForm>
</template>
